/**
 * Configuration Service
 *
 * Manages runtime configuration for the application.
 * Loads configuration from /config.json and provides typed access to settings.
 */

import { AppConfig, DEFAULT_CONFIG } from '../types/config';

class ConfigService {
  private config: AppConfig | null = null;
  private loadPromise: Promise<AppConfig> | null = null;

  /**
   * Load configuration from the server
   * Uses caching to avoid multiple fetches
   */
  async loadConfig(): Promise<AppConfig> {
    // Return cached config if already loaded
    if (this.config) {
      return this.config;
    }

    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.loadPromise = this.fetchConfig();
    this.config = await this.loadPromise;
    return this.config;
  }

  /**
   * Fetch configuration from server
   */
  private async fetchConfig(): Promise<AppConfig> {
    try {
      // Fetch with cache-busting to ensure fresh config
      const response = await fetch(`${process.env.PUBLIC_URL}/config.json?t=${Date.now()}`, {
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        console.warn('Failed to load config.json, using default configuration');
        return this.getDefaultConfig();
      }

      const config = await response.json();

      if (config.features?.enableDebugLogging) {
        console.log('🔧 Loaded configuration:', config);
      }

      return config;
    } catch (error) {
      console.error('Error loading configuration:', error);
      return this.getDefaultConfig();
    }
  }

  /**
   * Get default configuration with environment variable fallbacks
   */
  private getDefaultConfig(): AppConfig {
    // Detect if we're in development based on hostname
    const isDevelopment = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
       window.location.hostname === '127.0.0.1' ||
       window.location.hostname.includes('localhost'));

    // Use development config if in development, otherwise use production defaults
    const baseConfig: AppConfig = isDevelopment ? {
      environment: 'development',
      smartApi: {
        baseUrl: 'http://localhost:3000',
        loginPath: '/base/v1/logins',
        useProxy: true,
        proxyPath: '/api/smart'
      },
      swsApi: {
        baseUrl: 'http://localhost:3000',
        loginPath: '/login',
        useProxy: true,
        proxyPath: '/api/sws'
      },
      features: {
        enableOfflineMode: true,
        enableDebugLogging: true
      }
    } : DEFAULT_CONFIG;

    // Check for environment variables as additional fallback
    const envConfig: Partial<AppConfig> = {};

    // Support React environment variables if available
    if (typeof process !== 'undefined' && process.env) {
      if (process.env.REACT_APP_SMART_API_URL) {
        envConfig.smartApi = {
          ...baseConfig.smartApi,
          baseUrl: process.env.REACT_APP_SMART_API_URL
        };
      }
      if (process.env.REACT_APP_SWS_API_URL) {
        envConfig.swsApi = {
          ...baseConfig.swsApi,
          baseUrl: process.env.REACT_APP_SWS_API_URL
        };
      }
    }

    console.warn('Using default configuration. Environment:', isDevelopment ? 'development' : 'production');
    return {
      ...baseConfig,
      ...envConfig
    };
  }

  /**
   * Get current configuration
   * Throws if config not loaded yet
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call loadConfig() first.');
    }
    return this.config;
  }

  /**
   * Get Smart API URL based on current configuration
   */
  getSmartApiUrl(path: string = ''): string {
    const config = this.getConfig();
    if (config.smartApi.useProxy) {
      return `${config.smartApi.proxyPath}${path}`;
    }
    return `${config.smartApi.baseUrl}${path}`;
  }

  /**
   * Get SWS API URL based on current configuration
   */
  getSwsApiUrl(path: string = ''): string {
    const config = this.getConfig();
    if (config.swsApi.useProxy) {
      return `${config.swsApi.proxyPath}${path}`;
    }
    return `${config.swsApi.baseUrl}${path}`;
  }

  /**
   * Check if debug logging is enabled
   */
  isDebugEnabled(): boolean {
    return this.config?.features?.enableDebugLogging ?? true;
  }

  /**
   * Check if offline mode is enabled
   */
  isOfflineEnabled(): boolean {
    return this.config?.features?.enableOfflineMode ?? true;
  }

  /**
   * Get current environment
   */
  getEnvironment(): string {
    return this.config?.environment ?? 'development';
  }

  /**
   * Reload configuration from server
   */
  async reloadConfig(): Promise<AppConfig> {
    this.config = null;
    this.loadPromise = null;
    return this.loadConfig();
  }
}

// Export singleton instance
export const configService = new ConfigService();